import { focusWithin, allTabbableElements } from '../utils/PRIVATE_tabbableUtils';
import { isLogicalAncestor } from '../utils/UNSAFE_popupUtils';
import { getClientHints } from '../utils/PRIVATE_clientHints';

interface Modal {
  modalRef?: HTMLDivElement | null;
}

/**
 * CSS property backup type.
 */
type StyleBackup = Array<{
  property: string;
  value: string;
}>;

export default class ModalManager {
  /**
   * Array of ModalDescriptors managed by ModalManager
   */
  private modals: Modal[];
  /**
   * Backup of the main container's (body) CSS props potentially altered by Modal.
   */
  private styleBackup: StyleBackup;
  /**
   * A flag indicating that the ModalManager's focus handler is installed
   */
  private isFocusHandlerActive = false;
  /**
   * Browser (Firefox) detection
   */
  private browser = getClientHints().browser;
  private isFirefox = (): boolean => {
    return this.browser === 'firefox';
  };

  constructor() {
    this.modals = [];
    this.styleBackup = [];
  }

  /**
   * A focus handler to keep focus in the top-most modal.
   */
  private focusHandler = (event: FocusEvent) => {
    const topModal = this.getTopModal();
    const target = event.target as HTMLElement;

    // if there is an open modal and valid focus event target...

    // JET-76110
    // Exclusively Firefox fires 'focus' event when a tab receives initial focus (click out of FF and back).
    // This event includes 'Document' as a target. Some internal mechanism then probably picks lastly focused node.
    // However, without excluding it, this handler fouces first focusable element and prevents FF behavior.
    if (topModal && target && !(this.isFirefox() && target instanceof Document)) {
      const modalElement = topModal.modalRef;
      if (
        modalElement &&
        !modalElement.contains(target) &&
        !isLogicalAncestor(modalElement, target) &&
        !this._parentLayerContainsNode(modalElement, target)
      ) {
        // Focus is outside of the top modal container, let's try to fix it
        const tabbableElements = allTabbableElements(modalElement);
        if (tabbableElements.length > 0) {
          // re-directing focus to the element inside top modal
          focusWithin(modalElement);
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
  };

  /**
   * Registers a modal with ModalManager
   *
   * @param modal
   * @returns
   */
  push(modal: Modal) {
    const modalIndex = this._findModalIndex(modal);

    if (modalIndex !== -1) {
      // Do not try to register the same modal multiple times
      return;
    }

    this.modals.push(modal);

    // if this is the first modal in the container, block container scrolling
    if (this.modals.length === 1) {
      const ownerDocument = this._getOwnerDocument(modal.modalRef);
      const scrollContainer = ownerDocument.body;

      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollBarWidth > 1) {
        // in RTL, scrollbar is on the left
        const scrollBarLeft =
          Math.round(document.documentElement.getBoundingClientRect().left) +
          document.documentElement.scrollLeft;

        if (scrollBarLeft) {
          this.styleBackup.push({
            property: 'padding-left',
            value: scrollContainer.style.paddingLeft
          });
          scrollContainer.style.paddingLeft = `${scrollBarWidth}px`;
        } else {
          this.styleBackup.push({
            property: 'padding-right',
            value: scrollContainer.style.paddingRight
          });
          scrollContainer.style.paddingRight = `${scrollBarWidth}px`;
        }
      }

      this.styleBackup.push({ property: 'overflow', value: scrollContainer.style.overflow });
      this.styleBackup.push({ property: 'overflow-x', value: scrollContainer.style.overflowX });
      this.styleBackup.push({ property: 'overflow-y', value: scrollContainer.style.overflowY });

      scrollContainer.style.overflow = 'hidden';
    }

    // also install the focus handler when the first modal becomes active
    if (this.modals.length === 1 && !this.islegacyLayerSupportAvailable()) {
      document.addEventListener('focus', this.focusHandler, true);
      this.isFocusHandlerActive = true;
    }
  }

  /**
   * Unegisters a modal from ModalManager
   *
   * @param modal
   * @returns
   */
  pop(modal: Modal) {
    const modalIndex = this._findModalIndex(modal);

    if (modalIndex === -1) {
      // modal to be removed not found!'
      return;
    }

    if (modalIndex !== this.modals.length - 1) {
      // trying to remove modal out of order!
      return;
    }

    // remove the focus handler when the last modal is about to be removed
    if (this.isFocusHandlerActive && this.modals.length === 1) {
      document.removeEventListener('focus', this.focusHandler, true);
    }

    this.modals.pop();

    // if this was the last modal in the container, restore its overflow props
    if (this.modals.length === 0) {
      const ownerDocument = this._getOwnerDocument(modal.modalRef);
      const scrollContainer = ownerDocument.body;

      this.styleBackup.forEach(({ property, value }) => {
        scrollContainer.style.setProperty(property, value);
      });
    }
  }

  isTopModal(modal: Modal): boolean {
    return this.modals.length > 0 && this.modals[this.modals.length - 1] === modal;
  }

  getTopModal = (): Modal | null => {
    return this.modals.length > 0 ? this.modals[this.modals.length - 1] : null;
  };

  private _findModalIndex(modal: Modal): number {
    let index = -1;
    this.modals.forEach((elem, idx) => {
      if (elem === modal) {
        index = idx;
        return;
      }
    });
    return index;
  }

  private _getOwnerDocument(node: Node | null | undefined): Document {
    return (node && node.ownerDocument) || document;
  }

  /**
   * Checks of the modal's layer contains the target node
   * @param modalElement modal element
   * @param node target node
   * @returns true, if the modal layer contains the node
   */
  private _parentLayerContainsNode(modalElement: Element, node: Node): boolean {
    const parentLayer = modalElement.closest('[data-oj-layer]');
    if (parentLayer && parentLayer.contains(node)) {
      return true;
    }
    return false;
  }

  // TODO: replace with a proper API
  /**
   * Checks if the legacy popup service is available
   */
  private islegacyLayerSupportAvailable = () => {
    return document.getElementById('__oj_zorder_container') !== null;
  };
}
